import { apiFetch } from "@/lib/api-client";

/** A catalog product (list/summary form). */
export interface Product {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly categoryId: string | null;
  readonly unitId: string | null;
  /** A display image URL — hosted elsewhere; never uploaded/stored as file bytes. */
  readonly imageUrl: string | null;
  /** Oversell policy (EPIC-9): allow reservations beyond available stock. */
  readonly allowOversell: boolean;
  readonly active: boolean;
  /** Distinct warehouse names holding stock for this product's variants; empty when none. */
  readonly warehouseNames: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A product variant. `averageCost` is integer minor units (read-only). */
export interface ProductVariant {
  readonly id: string;
  readonly productId: string;
  readonly name: string;
  readonly sku: string | null;
  readonly barcode: string | null;
  readonly averageCost: number;
  readonly active: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A product with its variants (the detail view). */
export interface ProductDetail extends Product {
  readonly variants: ProductVariant[];
}

/**
 * One sellable variant with its product — a row of the order form's picker.
 *
 * The order form used to build this shape itself, one request per product; the
 * API now returns it directly so the picker searches the catalogue instead of
 * downloading it.
 */
export interface SellableVariant {
  readonly variantId: string;
  readonly variantName: string;
  readonly sku: string | null;
  readonly sellingPriceMinor: number;
  readonly productId: string;
  readonly productName: string;
  readonly imageUrl: string | null;
}

/** A keyset page of sellable variants. */
export interface SellableVariantPage {
  readonly data: SellableVariant[];
  readonly page: {
    readonly limit: number;
    readonly nextCursor: string | null;
    readonly hasMore: boolean;
  };
}

/** Query options for the sellable-variant search. */
export interface VariantSearchOptions {
  readonly q?: string;
  readonly limit?: number;
  readonly cursor?: string;
  /** Only variants holding a stock row in this warehouse. */
  readonly warehouseId?: string;
  /** Only variants with units left to sell. */
  readonly hasStock?: boolean;
}

/** A keyset page of products (api-conventions §5). */
export interface ProductPage {
  readonly data: Product[];
  readonly page: {
    readonly limit: number;
    readonly nextCursor: string | null;
    readonly hasMore: boolean;
  };
}

/** Query options for the products list. */
export interface ListOptions {
  readonly cursor?: string;
  /** Page size; the API clamps it to its own ceiling (100). */
  readonly limit?: number;
  /** `true` = active only, `false` = archived only, `"all"` = both. */
  readonly active?: boolean | "all";
  readonly q?: string;
  readonly categoryId?: string;
  /** Only products with a variant that has available stock (EPIC-9). */
  readonly hasStock?: boolean;
  /** Only products with a variant holding a stock row in this warehouse. */
  readonly warehouseId?: string;
}

function buildQuery(options: ListOptions): string {
  const params = new URLSearchParams();
  if (options.cursor !== undefined) params.set("cursor", options.cursor);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.active !== undefined) params.set("active", String(options.active));
  if (options.q !== undefined && options.q.length > 0) params.set("q", options.q);
  if (options.categoryId !== undefined) params.set("categoryId", options.categoryId);
  if (options.hasStock === true) params.set("hasStock", "true");
  if (options.warehouseId !== undefined) params.set("warehouseId", options.warehouseId);
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

/** Create/update product body. Send `null` to clear an optional field. */
export interface ProductInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly categoryId?: string | null;
  readonly unitId?: string | null;
  readonly imageUrl?: string | null;
  readonly allowOversell?: boolean;
}

/** Create/update variant body. */
export interface VariantInput {
  readonly name?: string;
  readonly sku?: string | null;
  readonly barcode?: string | null;
  readonly active?: boolean;
}

/** `GET /v1/products` — keyset-paginated products. */
export function listProducts(options: ListOptions = {}): Promise<ProductPage> {
  return apiFetch<ProductPage>(`/products${buildQuery(options)}`);
}

/**
 * `GET /v1/products/variants` — sellable variants across the catalogue.
 *
 * The search runs in the database, so this is asked once per keystroke rather
 * than used to cache the catalogue: the results are always current, and a
 * phone never waits on the whole list before it can search.
 */
export function searchSellableVariants(
  options: VariantSearchOptions = {},
): Promise<SellableVariantPage> {
  const params = new URLSearchParams();
  if (options.q !== undefined && options.q.length > 0) params.set("q", options.q);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.cursor !== undefined) params.set("cursor", options.cursor);
  if (options.warehouseId !== undefined) params.set("warehouseId", options.warehouseId);
  if (options.hasStock === true) params.set("hasStock", "true");
  const qs = params.toString();
  return apiFetch<SellableVariantPage>(`/products/variants${qs.length > 0 ? `?${qs}` : ""}`);
}

/** `GET /v1/products/{id}` — a product with its variants. */
export function getProduct(id: string): Promise<ProductDetail> {
  return apiFetch<ProductDetail>(`/products/${id}`);
}

/** `POST /v1/products` — create a product. */
export function createProduct(body: ProductInput): Promise<Product> {
  return apiFetch<Product>("/products", { method: "POST", body });
}

/** `PATCH /v1/products/{id}` — update a product. */
export function updateProduct(id: string, body: ProductInput): Promise<Product> {
  return apiFetch<Product>(`/products/${id}`, { method: "PATCH", body });
}

/** `DELETE /v1/products/{id}` — archive a product (soft-delete). */
export function archiveProduct(id: string): Promise<void> {
  return apiFetch<void>(`/products/${id}`, { method: "DELETE" });
}

/** `GET /v1/products/{id}/variants` — a product's variants. */
export function listVariants(productId: string): Promise<{ data: ProductVariant[] }> {
  return apiFetch<{ data: ProductVariant[] }>(`/products/${productId}/variants`);
}

/** `POST /v1/products/{id}/variants` — add a variant. */
export function createVariant(productId: string, body: VariantInput): Promise<ProductVariant> {
  return apiFetch<ProductVariant>(`/products/${productId}/variants`, { method: "POST", body });
}

/** `PATCH /v1/products/{id}/variants/{variantId}` — update a variant. */
export function updateVariant(
  productId: string,
  variantId: string,
  body: VariantInput,
): Promise<ProductVariant> {
  return apiFetch<ProductVariant>(`/products/${productId}/variants/${variantId}`, {
    method: "PATCH",
    body,
  });
}

/** The column mapping for a CSV import (values are header column names). */
export interface ProductImportMapping {
  readonly name: string;
  readonly description?: string;
  readonly categoryId?: string;
  readonly unitId?: string;
  /** When mapped, also creates the product's first variant. */
  readonly sku?: string;
  readonly barcode?: string;
}

/** One row's outcome in a CSV import. */
export interface ProductImportResultItem {
  readonly row: number;
  readonly ok: boolean;
  readonly productId?: string;
  readonly error?: { readonly code: string; readonly message: string };
}

/** `POST /v1/products/import` — import products from CSV with a column mapping. */
export function importProducts(
  csv: string,
  mapping: ProductImportMapping,
): Promise<{ results: ProductImportResultItem[] }> {
  return apiFetch<{ results: ProductImportResultItem[] }>("/products/import", {
    method: "POST",
    body: { csv, mapping },
  });
}
